/**
 * UserOnboardingEditor - Edit a single user's onboarding track and step state
 *
 * Renders inline after admin searches for a user. Loads state from server
 * and allows toggling track, individual steps, or full reset.
 *
 * @atomic @idempotent
 */
import { memo, useState, useEffect, useCallback } from 'react'
import { CheckIcon } from '@heroicons/react/24/outline'
import { onboardingAPI } from '../../services/api/onboarding'
import { useAsyncOperation } from '../../hooks/useAsyncOperation'
import { toast } from '../../services/toastService'

const ALL_STEPS = ['signed_up', 'spotted', 'favorited', 'messaged']
const TRACKS = ['visitor', 'vendor']

const UserOnboardingEditor = memo(function UserOnboardingEditor({ userId, email }) {
  const { execute, loading } = useAsyncOperation()
  const [state, setState] = useState(null)
  const [note, setNote] = useState('')

  const load = useCallback(async () => {
    await execute(() => onboardingAPI.adminGetUserState(userId), {
      onSuccess: (r) => setState(r.data),
      onError: () => toast.error('Failed to load onboarding state'),
    })
  }, [userId, execute])

  useEffect(() => { load() }, [load])

  const handleTrackChange = useCallback(async (track) => {
    await execute(() => onboardingAPI.adminSetUserState(userId, { track, adminNote: note || undefined }), {
      onSuccess: (r) => { setState(r.data); toast.success('Track updated') },
      onError: () => toast.error('Failed to update track'),
    })
  }, [userId, note, execute])

  const handleStepToggle = useCallback(async (step, currentlyDone) => {
    const next = { ...state?.completedSteps }
    if (currentlyDone) {
      delete next[step]
    } else {
      next[step] = Date.now()
    }
    await execute(() => onboardingAPI.adminSetUserState(userId, {
      completedSteps: next,
      adminNote: note || undefined,
    }), {
      onSuccess: (r) => { setState(r.data); toast.success('Steps updated') },
      onError: () => toast.error('Failed to update steps'),
    })
  }, [userId, state, note, execute])

  const handleReset = useCallback(async () => {
    if (!window.confirm(`Reset all onboarding steps for ${email}?`)) return
    await execute(() => onboardingAPI.adminSetUserState(userId, {
      completedSteps: {},
      adminNote: note || 'Reset by admin',
    }), {
      onSuccess: (r) => { setState(r.data); toast.success('Onboarding reset') },
      onError: () => toast.error('Failed to reset'),
    })
  }, [userId, email, note, execute])

  if (!state) return <p className="text-xs text-gray-400 py-2">{loading ? 'Loading…' : 'No data'}</p>

  return (
    <div className="border border-gray-100 rounded-lg p-4 space-y-3 bg-gray-50">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700 truncate">{email}</span>
        {state.adminNote && (
          <span className="text-xs text-amber-600 italic truncate max-w-[200px]" title={state.adminNote}>
            Note: {state.adminNote}
          </span>
        )}
      </div>

      {/* Track selector */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-500 w-14">Track</span>
        <div className="flex gap-1">
          {TRACKS.map(t => (
            <button
              key={t}
              type="button"
              onClick={() => handleTrackChange(t)}
              disabled={loading}
              className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                state.track === t
                  ? 'bg-primary text-white border-primary'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-primary'
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* Step toggles */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-gray-500 w-14">Steps</span>
        {ALL_STEPS.map(step => {
          const done = !!state.completedSteps?.[step]
          return (
            <button
              key={step}
              type="button"
              onClick={() => handleStepToggle(step, done)}
              disabled={loading}
              className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                done
                  ? 'bg-green-100 text-green-700 border-green-200'
                  : 'bg-white text-gray-400 border-gray-200 hover:border-gray-400'
              }`}
            >
              {done && <CheckIcon className="inline h-3 w-3 mr-1" aria-hidden="true" />}{step}
            </button>
          )
        })}
      </div>

      {/* Admin note + reset */}
      <div className="flex gap-2">
        <input
          type="text"
          value={note}
          onChange={e => setNote(e.target.value)}
          placeholder="Optional note…"
          className="flex-1 text-xs border border-gray-200 rounded px-2 py-1"
        />
        <button
          type="button"
          onClick={handleReset}
          disabled={loading}
          className="px-3 py-1 text-xs rounded border border-red-200 text-red-600 hover:bg-red-50 transition-colors"
        >
          Reset all
        </button>
      </div>
    </div>
  )
})

export default UserOnboardingEditor
